/*
 * The Risks screen's state.
 *
 * `score` is derived server-side from likelihood × impact and never stored separately — the DTO
 * says so — so nothing here recomputes it. What this file does derive is the *band*: the label a
 * person reads off the matrix, which has to be one rule applied everywhere or the register and the
 * matrix will disagree about which square a risk sits in.
 *
 * Accepting a risk is the consequential action, and it is not a status change. It is a signed-off
 * waiver with a reason and conditions, which is why `signOff` takes both and refuses neither.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, opsApi } from '../../../api';
import type { OpsRiskDto, OpsRiskInput, OpsRiskStatus, OpsStaffSummaryDto } from '../../../api';

export type RiskBand = 'critical' | 'high' | 'medium' | 'low';

/**
 * One rule, used by both the register and the matrix.
 *
 * Likelihood and impact are 1–5, so the product runs 1–25. The bands follow the shape every risk
 * matrix uses: the top-right corner is critical, the diagonal is high, and the bottom-left is low.
 */
export function bandOf(score: number): RiskBand {
  if (score >= 15) return 'critical';
  if (score >= 9) return 'high';
  if (score >= 4) return 'medium';
  return 'low';
}

export interface OpsRisksState {
  risks: OpsRiskDto[];
  staff: OpsStaffSummaryDto[];
  loading: boolean;
  loadError: string;
  error: string;
  busyId: string | null;
  canSignOff: boolean;
  create: (input: OpsRiskInput) => Promise<void>;
  update: (risk: OpsRiskDto, patch: Partial<OpsRiskInput>) => Promise<void>;
  setStatus: (risk: OpsRiskDto, status: OpsRiskStatus) => Promise<void>;
  signOff: (risk: OpsRiskDto, waiverReason: string, conditions: string) => Promise<void>;
  remove: (risk: OpsRiskDto) => Promise<void>;
  reload: () => Promise<void>;
}

export function useOpsRisks(token: string, eventSlug: string | undefined, role: string | null): OpsRisksState {
  const [risks, setRisks] = useState<OpsRiskDto[] | null>(null);
  const [staff, setStaff] = useState<OpsStaffSummaryDto[]>([]);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([
        opsApi.listRisks(token, eventSlug),
        opsApi.listStaff(token, eventSlug).catch(() => [] as OpsStaffSummaryDto[]),
      ]);
      setRisks(r);
      setStaff(s);
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'โหลดทะเบียนความเสี่ยงไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  const run = useCallback(async (id: string | null, fn: () => Promise<unknown>, fallback: string) => {
    setBusyId(id);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
    } finally {
      setBusyId(null);
    }
  }, [load]);

  return {
    risks: risks ?? [], staff,
    loading: risks === null && !loadError,
    loadError, error, busyId,
    // Mirrors the server: accepting a risk on the event's behalf is the owner's call, not staff's.
    canSignOff: role === 'owner',
    create: (input) => run(null, () => opsApi.createRisk(token, input, eventSlug), 'สร้างความเสี่ยงไม่สำเร็จ'),
    update: (risk, patch) =>
      run(risk.id, () => opsApi.updateRisk(token, risk.id, { ...patch, version: risk.version }, eventSlug), 'บันทึกไม่สำเร็จ'),
    setStatus: (risk, status) =>
      run(risk.id, () => opsApi.updateRisk(token, risk.id, { status, version: risk.version }, eventSlug), 'เปลี่ยนสถานะไม่สำเร็จ'),
    signOff: (risk, waiverReason, conditions) =>
      run(risk.id, () => opsApi.signOffRisk(token, risk.id, { waiverReason, conditions }, eventSlug), 'ลงนามรับความเสี่ยงไม่สำเร็จ'),
    remove: (risk) => run(risk.id, () => opsApi.deleteRisk(token, risk.id, risk.version, eventSlug), 'ลบไม่สำเร็จ'),
    reload: load,
  };
}
